import { Version } from '@environment/resources';

// Define the structure for a failed check
export interface CheckFailureResponse {
  checkName: string; // Category of the check
  version: string; // Version of the check logic
  reason: string; // Specific reason for the failure
}

const getSpeed = (
  p1: { x: number; y: number; time: number },
  p2: { x: number; y: number; time: number },
): number => {
  const distance = Math.hypot(p2.x - p1.x, p2.y - p1.y);

  const deltaTime = p2.time - p1.time;

  if (deltaTime === 0) {
    // If position is also the same, speed is 0.
    // If position changed instantly, speed is infinite.
    return distance === 0 ? 0 : Number.POSITIVE_INFINITY;
  }

  if (deltaTime < 0) {
    return 0;
  }

  return distance / deltaTime;
};

const directionChanged = (
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
): boolean => {
  return (
    Math.sign(p2.x - p1.x) !== Math.sign(p3.x - p2.x) ||
    Math.sign(p2.y - p1.y) !== Math.sign(p3.y - p2.y)
  );
};

export const trackMouseMovement = (
  mouseData: { x: number; y: number; time: number }[],
): boolean => {
  if (mouseData.length < 2) return true;

  const validData = mouseData.filter(
    point =>
      point &&
      typeof point.x === 'number' &&
      typeof point.y === 'number' &&
      typeof point.time === 'number',
  );

  if (validData.length < 3) return true;

  let speedChanges = 0;
  let directionChanges = 0;

  for (let i = 2; i < validData.length; i++) {
    const prev = validData[i - 1];
    const curr = validData[i];
    const beforePrev = validData[i - 2];

    const speedPrev = getSpeed(beforePrev, prev);
    const speedCurr = getSpeed(prev, curr);

    if (Math.abs(speedCurr - speedPrev) > 0.2) speedChanges++;
    if (directionChanged(beforePrev, prev, curr)) directionChanges++;
  }

  return speedChanges === 0 || directionChanges === 0;
};

export const analyzeUserBehavior = (
  mouseClickCountInput: number | undefined | null,
  mouseDataInput: { x: number; y: number; time: number }[] | undefined | null,
  keyPressCountInput: number | undefined | null,
  isMobileDeviceInput: boolean | undefined | null,
  fingerTouchCountInput: number | undefined | null,
  checkVersion = Version,
): { isBot: boolean; botChecksFailed: CheckFailureResponse[] } => {
  const mouseClickCount: number = mouseClickCountInput ?? 0;
  const mouseData: { x: number; y: number; time: number }[] = mouseDataInput ?? [];
  const keyPressCount: number = keyPressCountInput ?? 0;
  const isMobileDevice: boolean = isMobileDeviceInput ?? false;
  const isFingerTouchCount: number = fingerTouchCountInput ?? 0;

  const botChecksFailed: CheckFailureResponse[] = [];

  const { isAutomation, msg } = detectAutomationEnv(isMobileDevice);
  const unnaturalMouse = trackMouseMovement(mouseData);

  if (isAutomation)
    botChecksFailed.push({
      checkName: 'environment-check',
      version: checkVersion,
      reason: msg,
    });

  if (!isMobileDevice && mouseClickCount > 0 && unnaturalMouse) {
    botChecksFailed.push({
      checkName: 'mouse-movement',
      version: checkVersion,
      reason: 'Mouse movement pattern detected as bot',
    });
  }

  if (mouseData.length === 0 && keyPressCount === 0 && mouseClickCount === 0) {
    botChecksFailed.push({
      checkName: 'interaction-total',
      version: checkVersion,
      reason: 'No user interaction detected (mouse move, click, key press, touch)',
    });
  }

  if (isMobileDevice && isFingerTouchCount === 0 && mouseClickCount === 0) {
    botChecksFailed.push({
      checkName: 'interaction-mobile',
      version: checkVersion,
      reason: 'Interaction detected on mobile without primary methods (touch/click)',
    });
  }

  return {
    isBot: botChecksFailed.length > 0,
    botChecksFailed,
  };
};

export const detectAutomationEnv = (isMobile: boolean): { isAutomation: boolean; msg?: string } => {
  // 1. Guard Clause: Handle non-browser environments first.
  if (typeof navigator === 'undefined') {
    return {
      isAutomation: true,
      msg: 'Navigator object is undefined; likely a non-browser environment.',
    };
  }

  // 2. Define individual detection conditions for clarity.
  const hasWebdriverFlag: boolean = navigator.webdriver === true;

  // Check for Chrome/Chromium anomaly: presence of 'chrome' object and missing/empty languages.
  const lacksLanguages: boolean = !navigator.languages || navigator.languages.length === 0; // Use standard navigator.languages
  const isChromeLanguageAnomaly: boolean = 'chrome' in window && lacksLanguages; // Assumes 'window' exists

  // Check for empty plugins list on non-mobile browsers.
  const hasEmptyPlugins: boolean = navigator.plugins?.length === 0;
  const isEmptyPluginNonMobile: boolean = hasEmptyPlugins && !isMobile;

  const isAutomationDetected: boolean =
    hasWebdriverFlag || isChromeLanguageAnomaly || isEmptyPluginNonMobile;

  // 4. Return the result based on the combined conditions.
  if (isAutomationDetected) {
    return {
      isAutomation: true,
      msg: 'Widget is running on test environment',
    };
  }

  // 5. If no conditions met, assume not automation.
  return {
    isAutomation: false,
  };
};

export const getLatestEntries = (
  mouseData: { x: number; y: number; time: number }[],
  isFull: boolean,
  maxSize: number,
  currentIndex,
  n: number,
): { x: number; y: number; time: number }[] => {
  if (!Array.isArray(mouseData) || mouseData.length === 0) {
    return [];
  }

  const count = Math.min(n, isFull ? maxSize : currentIndex);
  const startIdx = (currentIndex - count + maxSize) % maxSize;

  if (startIdx < currentIndex) {
    return mouseData.slice(startIdx, currentIndex);
  } else {
    return [...mouseData.slice(startIdx), ...mouseData.slice(0, currentIndex)];
  }
};
